import { ArrowLeft, Loader2, Minus, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { useCategories } from "@/api/category-query";
import { createVendorProduct, useProducts } from "@/api/products-query";
import {
	stashPublicInvoicePayment,
	useCreatePublicContract,
	useInitiatePublicContractPayment
} from "@/api/public-contract-query";
import { usePrivateAxios } from "@/api/use-private-axios";
import { useCurrencies } from "@/api/util";
import UploadFile from "@/components/form/file-input";
import { ExpandingSearchBar } from "@/components/dashboard/search-bar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumInputSeparated } from "@/components/ui/num-input-separated";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { cn, formatCurrency } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/vendor/invoices/create")({
	component: RouteComponent
});

const buyerSchema = z.object({
	firstName: z.string().min(1, "First name is required"),
	lastName: z.string().min(1, "Last name is required"),
	email: z.string().email("Enter a valid email").refine((value) => {
		const tld = value.split("@")[1]?.split(".").pop() ?? "";
		return /^[a-z]{2,6}$/i.test(tld);
	}, "Enter a valid email (e.g. buyer@gmail.com)"),
	phoneNumber: z
		.string()
		.min(1, "Phone number is required")
		.refine((value) => {
			const digits = value.replace(/\D/g, "");
			if (digits.length < 10 || digits.length > 15)
				return false;
			const national = digits.startsWith("234")
				? digits.slice(3)
				: digits.startsWith("0")
					? digits.slice(1)
					: digits;
			return /^[789][01]\d{8}$/.test(national);
		}, "Enter a valid Nigerian number (e.g. 09012345678)"),
	buyerState: z.string().optional(),
	buyerCity: z.string().optional(),
	buyerAddress: z.string().optional()
});

type BuyerFormValues = z.infer<typeof buyerSchema>;

/** Accept local NG numbers (0901…) and normalize to +234 for payment APIs. */
function normalizeNgPhone(phone: string) {
	const trimmed = phone.trim();
	const digits = trimmed.replace(/\D/g, "");
	if (!digits)
		return trimmed;
	if (digits.startsWith("234") && digits.length >= 13)
		return `+${digits}`;
	if (digits.startsWith("0") && digits.length >= 11)
		return `+234${digits.slice(1)}`;
	if (digits.length === 10 && /^[789]/.test(digits))
		return `+234${digits}`;
	if (trimmed.startsWith("+"))
		return `+${digits}`;
	return trimmed.startsWith("+") ? trimmed : `+${digits}`;
}

const newProductSchema = z.object({
	name: z.string().min(3, "Name must be at least 3 characters"),
	description: z.string().min(10, "Description must be at least 10 characters"),
	price: z.coerce.string().refine(val => !isNaN(Number(val)) && Number(val) > 0, {
		message: "Price must be greater than 0"
	}),
	contractQuantity: z.coerce.string().refine(val => !isNaN(Number(val)) && Number(val) > 0, {
		message: "Enter a quantity"
	}),
	quantity: z.coerce.string().refine(val => !isNaN(Number(val)) && Number(val) > 0, {
		message: "Enter a quantity"
	}),
	photos: z.string().min(1, "At least one photo is required"),
	categoryIds: z.array(z.string()).min(1, "Pick at least one category")
});

type NewProductFormValues = z.infer<typeof newProductSchema>;

type SelectedCatalogProduct = {
	productId: string;
	name: string;
	price: number;
	quantity: number;
	photo?: string;
};

function RouteComponent() {
	const navigate = useNavigate();
	const { data: productsData, isPending: productsLoading } = useProducts();
	const createInvoice = useCreatePublicContract();
	const initiatePayment = useInitiatePublicContractPayment();
	const [search, setSearch] = useState("");
	const [selected, setSelected] = useState<Record<string, SelectedCatalogProduct>>({});
	const [newProductOpen, setNewProductOpen] = useState(false);

	const form = useForm<BuyerFormValues>({
		resolver: zodResolver(buyerSchema),
		defaultValues: {
			firstName: "",
			lastName: "",
			email: "",
			phoneNumber: "",
			buyerState: "",
			buyerCity: "",
			buyerAddress: ""
		}
	});

	const products = useMemo(() => {
		const items = (productsData?.items ?? []).filter(
			p => p.status !== "INACTIVE" && Number(p.quantity) > 0
		);
		if (!search.trim())
			return items;
		const q = search.toLowerCase();
		return items.filter(p => p.name.toLowerCase().includes(q));
	}, [productsData, search]);

	const selectedList = Object.values(selected);
	const lineCount = selectedList.length;
	const catalogIds = useMemo(() => new Set(products.map(p => p.id)), [products]);
	const createdOnInvoice = selectedList.filter(item => !catalogIds.has(item.productId));
	const isSubmitting = createInvoice.isPending || initiatePayment.isPending;

	const upsertProduct = (product: { id: string; name: string; price: string | number; photos?: string[] }, delta: number) => {
		setSelected((prev) => {
			const existing = prev[product.id];
			const nextQty = (existing?.quantity ?? 0) + delta;
			if (nextQty <= 0) {
				const { [product.id]: _, ...rest } = prev;
				return rest;
			}
			return {
				...prev,
				[product.id]: {
					productId: product.id,
					name: product.name,
					price: Number(product.price) || 0,
					quantity: nextQty,
					photo: product.photos?.[0]
				}
			};
		});
	};

	const buildAutoTitleDescription = (values: BuyerFormValues) => {
		const buyerName = `${values.firstName} ${values.lastName}`.trim();
		const title = `Invoice for ${buyerName}`;
		const lines = selectedList.map(item => `${item.quantity}× ${item.name}`);
		const description = lines.length
			? `Invoice items: ${lines.join(", ")}`
			: `Invoice for ${buyerName} (${values.email})`;
		return { title, description };
	};

	const onSubmit = async (values: BuyerFormValues) => {
		if (!lineCount) {
			toast.error("Add at least one product to the invoice");
			return;
		}

		const phoneNumber = normalizeNgPhone(values.phoneNumber);
		const { title, description } = buildAutoTitleDescription(values);

		try {
			const contract = await createInvoice.mutateAsync({
				title,
				description,
				paymentOption: "safe_haven",
				buyerInfo: {
					firstName: values.firstName,
					lastName: values.lastName,
					email: values.email,
					phoneNumber
				},
				products: selectedList.map(item => ({
					productId: item.productId,
					quantity: item.quantity
				}))
			});

			if (!contract?.id) {
				toast.error("Invoice created but response was missing an id");
				return;
			}

			try {
				const payment = await initiatePayment.mutateAsync({
					contractId: contract.id,
					payload: {
						email: values.email,
						firstName: values.firstName,
						lastName: values.lastName,
						phoneNumber,
						paymentOption: "safe_haven",
						buyerState: values.buyerState || undefined,
						buyerCity: values.buyerCity || undefined,
						buyerAddress: values.buyerAddress || undefined
					}
				});
				stashPublicInvoicePayment(contract.id, payment);
				toast.success("Invoice created with payment details");
			}
			catch {
				toast.message("Invoice created — open it to load bank details");
			}

			navigate({
				to: "/vendor/invoices/$invoiceid",
				params: { invoiceid: contract.id }
			});
		}
		catch {
			/* create toast handled in mutation */
		}
	};

	return (
		<section className="mx-auto w-full max-w-5xl space-y-4 pb-24 sm:space-y-6 sm:pb-16">
			<div className="flex items-center gap-3 pt-2 sm:pt-4">
				<Button asChild variant="ghost" size="icon" className="shrink-0">
					<Link to="/vendor/invoices">
						<ArrowLeft className="h-4 w-4" />
					</Link>
				</Button>
				<div className="min-w-0">
					<h1 className="text-lg font-semibold sm:text-xl">Create invoice</h1>
					<p className="text-sm text-muted-foreground">
					</p>
				</div>
			</div>

			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
					<Card>
						<CardHeader className="flex flex-col items-stretch gap-3 space-y-0 p-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:p-6">
							<CardTitle className="text-base">
								Products
								{lineCount > 0 && (
									<span className="ml-2 text-sm font-normal text-muted-foreground">
										(
										{selectedList.reduce((sum, item) => sum + item.quantity, 0)}
										{" "}
										items)
									</span>
								)}
							</CardTitle>
							<div className="flex w-full items-center gap-2 sm:w-auto">
								<ExpandingSearchBar
									id="invoice-catalog-search"
									placeholder="Search catalog"
									value={search}
									onChange={e => setSearch(e.target.value)}
									className="min-w-0 flex-1 sm:flex-none"
								/>
								<Dialog open={newProductOpen} onOpenChange={setNewProductOpen}>
									<DialogTrigger asChild>
										<Button type="button" variant="outline" size="sm" className="shrink-0">
											<Plus className="h-4 w-4 mr-1" />
											New product
										</Button>
									</DialogTrigger>
									<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
										<DialogHeader>
											<DialogTitle>Add new product</DialogTitle>
											<DialogDescription>
												Saves to your catalog and adds to this invoice.
											</DialogDescription>
										</DialogHeader>
										<NewProductDialogForm
											onAdd={(product) => {
												if (document.activeElement instanceof HTMLElement) {
													document.activeElement.blur();
												}
												setSelected(prev => ({
													...prev,
													[product.productId]: product
												}));
												setNewProductOpen(false);
												toast.success("Product saved to catalog and added to this invoice");
											}}
										/>
									</DialogContent>
								</Dialog>
							</div>
						</CardHeader>
						<CardContent className="space-y-4 p-4 pt-0 sm:p-6 sm:pt-0">
							{createdOnInvoice.length > 0 && (
								<div className="space-y-2">
									<p className="text-sm font-medium text-muted-foreground">New products on this invoice</p>
									<div className="grid gap-2">
										{createdOnInvoice.map(product => (
											<div
												key={product.productId}
												className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 p-3"
											>
												<div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-white">
													{product.photo
														? (
																<img
																	src={product.photo}
																	alt={product.name}
																	className="h-full w-full object-cover"
																/>
															)
														: (
																<div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
																	New
																</div>
															)}
												</div>
												<div className="min-w-0 flex-1">
													<p className="truncate font-medium">{product.name}</p>
													<p className="text-sm text-muted-foreground">
														{product.quantity}
														×
														{" "}
														{formatCurrency(product.price, "NGN")}
													</p>
												</div>
												<Button
													type="button"
													variant="ghost"
													size="icon"
													className="shrink-0 text-destructive"
													onClick={() => {
														setSelected((prev) => {
															const { [product.productId]: _, ...rest } = prev;
															return rest;
														});
													}}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
										))}
									</div>
								</div>
							)}

							{productsLoading
								? (
										<div className="flex justify-center py-10">
											<Loader2 className="h-8 w-8 animate-spin text-primary" />
										</div>
									)
								: (
										<div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2 lg:grid-cols-[repeat(auto-fill,minmax(220px,1fr))]">
											{products.map((product) => {
												const qty = selected[product.id]?.quantity ?? 0;
												return (
													<div
														key={product.id}
														className={cn(
															"space-y-3 rounded-xl border bg-gray-50 p-3",
															qty > 0 && "border-primary ring-1 ring-primary/30"
														)}
													>
														<div className="h-32 overflow-hidden rounded-lg bg-white">
															<img
																src={product.photos?.[0] || "/placeholder.svg"}
																alt={product.name}
																className="h-full w-full object-cover"
															/>
														</div>
														<div>
															<p className="truncate font-medium">{product.name}</p>
															<p className="text-sm text-muted-foreground">
																{formatCurrency(Number(product.price) || 0, "NGN")}
															</p>
														</div>
														<div className="flex items-center justify-between">
															{qty > 0
																? (
																		<div className="inline-flex items-center overflow-hidden rounded-lg border">
																			<Button
																				type="button"
																				variant="ghost"
																				size="icon"
																				className="h-8 w-8"
																				onClick={() => upsertProduct(product, -1)}
																			>
																				<Minus className="h-3.5 w-3.5" />
																			</Button>
																			<span className="w-8 text-center text-sm font-medium">{qty}</span>
																			<Button
																				type="button"
																				variant="ghost"
																				size="icon"
																				className="h-8 w-8"
																				onClick={() => upsertProduct(product, 1)}
																			>
																				<Plus className="h-3.5 w-3.5" />
																			</Button>
																		</div>
																	)
																: (
																		<Button
																			type="button"
																			size="sm"
																			onClick={() => upsertProduct(product, 1)}
																		>
																			Add
																		</Button>
																	)}
														</div>
													</div>
												);
											})}
										</div>
									)}
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="p-4 sm:p-6">
							<CardTitle className="text-base">Buyer details</CardTitle>
						</CardHeader>
						<CardContent className="grid gap-4 p-4 pt-0 sm:grid-cols-2 sm:p-6 sm:pt-0">
							<FormField
								control={form.control}
								name="firstName"
								render={({ field }) => (
									<FormItem>
										<FormLabel>First name</FormLabel>
										<FormControl>
											<Input placeholder="Jane" {...field} autoComplete="given-name" />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="lastName"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Last name</FormLabel>
										<FormControl>
											<Input placeholder="Doe" {...field} autoComplete="family-name" />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Email</FormLabel>
										<FormControl>
											<Input type="email" placeholder="buyer@email.com" {...field} autoComplete="email" />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="phoneNumber"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Phone</FormLabel>
										<FormControl>
											<Input placeholder="08012345678" inputMode="tel" {...field} autoComplete="tel" />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</CardContent>
					</Card>

					<div className="sticky bottom-0 z-10 -mx-4 mt-4 flex gap-2 border-t bg-gray-100/95 p-3 backdrop-blur sm:bottom-4 sm:mx-0 sm:justify-end sm:rounded-xl sm:border sm:bg-white/95">
						<Button asChild type="button" variant="outline" className="flex-1 sm:flex-none">
							<Link to="/vendor/invoices">Cancel</Link>
						</Button>
						<Button type="submit" className="flex-1 sm:flex-none" disabled={isSubmitting || !lineCount}>
							{isSubmitting
								? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											Creating…
										</>
									)
								: "Create invoice"}
						</Button>
					</div>
				</form>
			</Form>
		</section>
	);
}

function NewProductDialogForm({
	onAdd
}: {
	onAdd: (product: SelectedCatalogProduct) => void;
}) {
	const categoryQuery = useCategories();
	const currenciesQuery = useCurrencies();
	const privateAxios = usePrivateAxios();
	const queryClient = useQueryClient();
	const [files, setFiles] = useState<File[] | null>(null);
	const [saving, setSaving] = useState(false);

	const form = useForm<NewProductFormValues>({
		resolver: zodResolver(newProductSchema),
		defaultValues: {
			name: "",
			description: "",
			price: "",
			contractQuantity: "1",
			quantity: "1",
			photos: "",
			categoryIds: []
		}
	});

	const handleSubmit = async (data: NewProductFormValues) => {
		if (!files?.length) {
			form.setError("photos", { message: "At least one photo is required" });
			return;
		}

		const categoryId = data.categoryIds[data.categoryIds.length - 1];
		if (!categoryId) {
			form.setError("categoryIds", { message: "Pick at least one category" });
			return;
		}

		const currencyId = currenciesQuery.data?.data.find(c => c.name === "Naira")?.id;
		if (!currencyId) {
			toast.error("Could not load currency. Try again in a moment.");
			return;
		}

		setSaving(true);
		try {
			const created = await createVendorProduct(privateAxios, {
				name: data.name,
				description: data.description,
				categoryIds: categoryId,
				price: Number(data.price),
				quantity: Number(data.quantity),
				currencyId,
				files: [...files]
			});
			queryClient.invalidateQueries({ queryKey: ["products"] });
			onAdd({
				productId: created.id,
				name: created.name || data.name,
				price: Number(created.price) || Number(data.price) || 0,
				quantity: Number(data.contractQuantity) || 1,
				photo: created.photos?.[0]
			});
		}
		catch (error: any) {
			const message = error?.response?.data?.message;
			toast.error(
				Array.isArray(message)
					? message.filter(Boolean).join(". ")
					: message || error?.message || "Failed to save product"
			);
		}
		finally {
			setSaving(false);
		}
	};

	return (
		<Form {...form}>
			<form
				onSubmit={(event) => {
					event.preventDefault();
					event.stopPropagation();
					void form.handleSubmit(handleSubmit)(event);
				}}
				className="space-y-4"
			>
				<UploadFile
					files={files}
					setFiles={(next) => {
						setFiles(next);
						form.setValue("photos", next?.[0]?.name ?? "", { shouldValidate: true });
					}}
					className="outline-dashed outline-1 outline-primary hover:bg-primary/20"
					name="photos"
					control={form.control}
				/>

				<FormField
					control={form.control}
					name="name"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Name</FormLabel>
							<FormControl>
								<Input placeholder="Custom leather bag" {...field} autoComplete="off" name="productName" />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="price"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Price</FormLabel>
							<FormControl>
								<NumInputSeparated
									currency="NGN"
									placeholder="Enter price"
									aria-label="Price"
									value={field.value ?? ""}
									onChange={field.onChange}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<div className="grid gap-4 sm:grid-cols-2">
					<FormField
						control={form.control}
						name="contractQuantity"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Qty on this invoice</FormLabel>
								<FormControl>
									<NumInputSeparated
										placeholder="1"
										aria-label="Qty on this invoice"
										value={field.value ?? ""}
										onChange={field.onChange}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="quantity"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Total qty in stock</FormLabel>
								<FormControl>
									<NumInputSeparated
										placeholder="1"
										aria-label="Total qty in stock"
										value={field.value ?? ""}
										onChange={field.onChange}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<FormField
					control={form.control}
					name="categoryIds"
					render={({ field }) => (
						<FormItem>
							<fieldset className="space-y-2">
								<legend className="text-sm font-medium">Category</legend>
								<div className="flex flex-wrap gap-2">
									{categoryQuery.isPending
										? (
												<>
													<Skeleton className="h-6 w-20 animate-pulse" />
													<Skeleton className="h-6 w-20 animate-pulse" />
													<Skeleton className="h-6 w-20 animate-pulse" />
												</>
											)
										: (
												categoryQuery.data?.data.map(category => (
													<button
														type="button"
														key={category.id}
														className={cn(
															"cursor-pointer rounded-2xl border border-primary bg-primary/10 px-2 py-1",
															{
																"bg-primary text-white": field.value?.includes(category.id)
															}
														)}
														onClick={() => {
															const values = field.value || [];
															const next = values.includes(category.id)
																? values.filter(id => id !== category.id)
																: [...values, category.id];
															field.onChange(next);
														}}
													>
														<p className="text-sm">{category.title}</p>
													</button>
												))
											)}
								</div>
							</fieldset>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="description"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Description</FormLabel>
							<FormControl>
								<Textarea placeholder="Product details…" rows={3} {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<DialogFooter className="gap-2 sm:gap-0">
					<Button type="submit" disabled={saving}>
						{saving
							? (
									<>
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										Saving…
									</>
								)
							: "Add to invoice"}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
}
